/**
 * @component CandidateModal
 * @description Modal para crear o editar un candidato en el sistema. Permite ingresar los datos personales,
 * asociar la certificación, colegio, rango de fechas y estatus. Realiza validaciones y operaciones
 * asíncronas contra la API (crear/editar usuarios y certificaciones).
 *
 * @param {Object} props - Props del componente.
 * @param {number} props.candidate_id - ID del candidato (0 para nuevo).
 * @param {number} props.certification_id - ID de la certificación asociada.
 * @param {string} props.action - Acción actual ("Nuevo" o "Editar").
 * @param {Function} props.handleClose - Función para cerrar el modal.
 * @returns {JSX.Element}
 */

import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Tabs, Tab } from 'react-bootstrap';

import { alert, errorNotification, extractUsername, getCatalog, handleApiError, isEmailValid, successNotification } from 'utils';
import { createUser, fetchUsers, updateUser } from 'api/users/users';
import { fetchColleges } from 'api/colleges/colleges';
import { createCertification, fetchCertificationById, updateCertificationDatesById } from 'api/certifications/certifications';

import SelectColleges from 'components/select/SelectColleges';
import DateRangePicker from 'components/datepicker/DateRangePicker';
import { formatDateToSQL } from 'utils';
import { uploadCandidates } from 'api/files/files';

const CandidateModal = ({ candidate_id, certification_id, action, handleClose }) => {
    const token = useSelector(state => state.authentication.user?.token);

    const [formCollege, setFormCollege] = useState({});
    const [formUser, setFormUser] = useState({ name: '', lastname: '', email: '', username: '', role_id: 4 });
    const [bulkForm, setBulkForm] = useState({ first_name: '', last_name: '', email: '', type: '', file: null });
    const [selectedFile, setSelectedFile] = useState(null);


    const [selectedCertification, setSelectedCertification] = useState("");
    const [selectedCertificationBulk, setSelectedCertificationBulk] = useState("");
    const [selectedType, setSelectedType] = useState("");
    const [selectedCandidate, setSelectedCandidate] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [sharedCollege, setSharedCollege] = useState(0);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [dateRangeBulk, setDateRangeBulk] = useState({ start: null, end: null });
    const [activeTab, setActiveTab] = useState('individual');

    const fileInputRef = useRef(null);

    const handleCertificationChange = (e) => setSelectedCertification(e.target.value);
    const handleCertificationBulkChange = (e) => setSelectedCertificationBulk(e.target.value);

    const handleCandidateChange = (e) => setSelectedCandidate(e.target.value);
    const handleTypeChange = (e) => setSelectedType(e.target.value);
    const handleStatusChange = (e) => setSelectedStatus(e.target.value);
    
    const handleDateChange = (range) => setDateRange(range);
    const handleDateBulkChange = (range) => setDateRangeBulk(range);
    
    const handleChangeBulk = (e) => setBulkForm({ ...bulkForm, [e.target.name]: e.target.value });

    /**
     * Carga datos de usuario y certificación si el candidato ya existe.
     */
    useEffect(() => {
        if (token && candidate_id > 0) {
            loadUserById(candidate_id);
            loadCertificationById(certification_id);
        }
        // eslint-disable-next-line
    }, [token, candidate_id]);

  
    /**
     * Maneja la creación de un nuevo usuario (certified, candidato individual o carga masiva).
     * Utiliza el endpoint unificado del backend para decidir automáticamente el tipo de usuario.
     *
     * @async
     * @function handleCreateUser
     * @returns {Promise<void>}
     */
    const handleCreateUser = async (payload) => {
        try {
            const { data } = await createUser(payload, token);
            handleCreateCertification(data.user_id);
        } catch (error) {
            handleApiError(error);
        }
    };

    /** Crea la certificación asociada al candidato. */
    const handleCreateCertification = async (user_id) => {
        try {
            const { start, end } = dateRange;
            const data = {
                user_id,
                college_id: sharedCollege.value,
                catalog_id: selectedCertification,
                certification_type: selectedType,
                candidate_type: selectedCandidate,
                status: 0,
                assigned_date: formatDateToSQL(start),
                closed_date: formatDateToSQL(end)
            };
            const response = await createCertification(data, token);
            if (response.data.status === 201) {
                successNotification("Candidato registrado con éxito.");
                handleCloseModal();
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    /** Carga los datos de un usuario por ID. */
    const loadUserById = async (id) => {
        try {
            const { data } = await fetchUsers({ id }, token);
            setSelectedStatus(data.status.toString());
            setFormUser(data);
        } catch (error) {
            handleApiError(error);
        }
    };

    /** Carga los datos de la certificación asociada. */
    const loadCertificationById = async (id) => {
        try {
            const response = await fetchCertificationById(id, token);
            if (response.data.status === 200) {
                const cert = response.data.message;
                setSelectedCertification(cert.catalog_id.toString());
                setSelectedType(cert.certification_type.toString());
                setSelectedCandidate(cert.candidate_type.toString());
                loadCollegeById(cert.college_id);
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    /**
     * Carga un colegio desde la API según su ID.
     * @param {number} id - ID del colegio
     */
    const loadCollegeById = async (id) => {
        try {
            const { data } = await fetchColleges({ id }, token);
            setFormCollege(data);
        } catch (error) {
            handleApiError(error);
        }
    };

    /** Actualiza la certificación con nuevas fechas si se han seleccionado. */
    const handleUpdateCertification = async () => {
        try {
            const { start, end } = dateRange;
            if (start && end) {
                const data = {
                    assigned_date: formatDateToSQL(start),
                    closed_date: formatDateToSQL(end)
                };
                const response = await updateCertificationDatesById(certification_id, data, token);
                if (response.data.status === 200) {
                    successNotification("Candidato actualizado con éxito.");
                    handleCloseModal();
                }
            } else {
                successNotification("Candidato actualizado con éxito.");
                handleCloseModal();
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    /** Actualiza los datos del usuario y continúa con la actualización de certificación si es exitoso. */
    const handleUpdateUser = async (id) => {
        try {
            const data = {
                name: formUser.name,
                lastname: formUser.lastname,
                role_id: 4,
                status: selectedStatus
            };

            const { data: responseData } = await updateUser(id, data, token);

            if (responseData?.message) {
                handleUpdateCertification(); // solo se ejecuta si el backend devolvió mensaje exitoso
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    /**
     * @function fetchUploadFiles
     * @description
     * Envía el archivo al backend mediante API y maneja la respuesta.
     * @param {FormData} form - FormData con el archivo.
     * @param {string} newFileName - Nombre del archivo que se cargará.
     */
    const fetchUploadFiles = async (e, form, newFileName) => {
        e.preventDefault();
        if (e.target.form.checkValidity()) {
            if (isEmailValid(bulkForm.email)) {
                try {
                    const response = await uploadCandidates(form, token);
                    if (response.data.status === 200) {
                        successNotification("Archivo cargado correctamente.");
                        handleBulkUpload(response.data.output_file);
                    } else {
                        errorNotification("No es posible subir el archivo.");
                    }
                } catch (error) {
                    handleApiError(error);
                }
            } else {
               errorNotification("El formato de email no es válido."); 
            }
        } else {
            errorNotification("Faltan campos requeridos.");
        }
    };

    /**
	 * @function handleFileReset
	 * @description
	 * Resetea el archivo seleccionado.
	 */
	const handleFileReset = () => {
		setSelectedFile(null);
		fileInputRef.current.value = '';
	};

    /** Ejecuta la acción principal según el modo (Nuevo o Editar). */
    const handleActionUser = (e) => {
        e.preventDefault();
        if (e.target.form.checkValidity()) {
            if (isEmailValid(formUser.email)) {
                if (action === "Nuevo") {
                    const { start, end } = dateRange;
                    if (start && end) {
                        const data = {
                            role_id: formUser.role_id,
                            username: formUser.username,
                            email: formUser.email,
                            name: formUser.name,
                            lastname: formUser.lastname,
                            certification_type: getCatalog(Number(selectedCertification)),
                            assigned_date: formatDateToSQL(start),
                            closed_date: formatDateToSQL(end),
                            college: sharedCollege.label
                        };
                        handleCreateUser(data);
                    } else {
                        errorNotification("Es necesario seleccionar las fechas.");
                    }
                } else {
                    handleUpdateUser(candidate_id);
                }
            } else {
                errorNotification("El formato de email no es válido.");
            }
        } else {
            errorNotification("Faltan campos requeridos.");
        }
    };

    // Handlers de formularios
    const handleChangeFormUser = (e) => setFormUser({ ...formUser, [e.target.name]: e.target.value });
    const handleChangeEmail = (e) => {
        const { name, value } = e.target;
        if (name === 'email') {
            const username = extractUsername(value);
            setFormUser(prev => ({ ...prev, username, email: value }));
        }
    };
   

    /** Cierra el modal y limpia el formulario. */
    const handleCloseModal = () => {
        document.getElementById('candidateModal')?.classList.remove('show');
        document.querySelector('.modal-backdrop')?.remove();
        document.getElementById('candidateModal').style.display = 'none';
        cleanForm();
        handleClose();
    };

    /** Limpia el formulario a valores por defecto. */
    const cleanForm = () => {
        setFormUser({});
        setBulkForm({});
        setSelectedCertification("");
        setSelectedCertificationBulk("");
        setSelectedCandidate("");
        setSelectedStatus("");
        setSelectedType("");
    };

    const handleBulkUpload = async (bulk_mail) => {
        const { start, end } = dateRangeBulk;
        if (start && end) {
            const payload = {
                bulk_mail: bulk_mail,
                email: bulkForm.email,
                name: bulkForm.first_name,
                lastname: bulkForm.last_name,
                certification_type: getCatalog(Number(selectedCertificationBulk)),
                assigned_date: formatDateToSQL(start),
                closed_date: formatDateToSQL(end),
                college: sharedCollege.label
            };
            try {
                const { data } = await createUser(payload, token);
                alert("¡Exito!", data.message, "success");
                handleFileReset();
                handleCloseModal();
            } catch (error) {
                handleApiError(error);
            }
        } else {
            errorNotification("Es necesario seleccionar las fechas.");
        }        
    };

    const handleFileChange = (event) => {
        const selected = event.target.files[0];
        if (selected) {
            const fileName = selected.name.toLowerCase();
            if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) {
                setSelectedFile(selected);
            } else {
                errorNotification('Formato de archivo no válido. Usa .xls o .xlsx.');
                setSelectedFile(null);
            }
        }
    };

    const handleSubmit = async (e) => {
        if (activeTab === 'individual') {
            handleActionUser(e);
        } else {
            if (!selectedFile) {
                errorNotification("Selecciona un archivo válido antes de continuar.");
                return;
            }
            const form = new FormData();
            form.append('uploaded_file', selectedFile);
            fetchUploadFiles(e, form);
        }
    };


    return(
        <div id="candidateModal" className="modal modal-cover fade" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{action} Candidato</h5>
                        <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                    </div>
                    <div className="modal-body">
                        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} id="candidate-tabs">
                            <Tab eventKey="individual" title="Usuario individual">
                                <div className="mt-3">
                                    <form id="formCandidate" className="was-validated" >
                                        <div className="row row-space-12">
                                            <div className="col-xl-12">
                                                <div className="mb-3 row" >
                                                    <div className="col-6">
                                                        <label className="form-label">Nombres</label>
                                                        <input className="form-control" placeholder="Nombres"
                                                            id="name"
                                                            name="name"
                                                            type='text'
                                                            value={formUser.name || ''}
                                                            onChange={handleChangeFormUser}
                                                            required
                                                            pattern=".*\S+.*" />
                                                        <div className="invalid-feedback" id="">
                                                            Campo requerido
                                                        </div>	
                                                    </div>
                                                    <div className="col-6">
                                                        <label className="form-label">Apellidos</label>
                                                        <input className="form-control" placeholder="Apellidos"
                                                            id="lastname"
                                                            name="lastname"
                                                            required
                                                            value={formUser.lastname || ''}
                                                            onChange={handleChangeFormUser}
                                                            pattern=".*\S+.*"/>
                                                        <div className="invalid-feedback" id="">
                                                            Campo requerido
                                                        </div>	
                                                    </div>
                                                </div>
                                                <div className="mb-3 row" >
                                                    <div className="col-6">
                                                        <label className="form-label">Email</label>
                                                        <input className="form-control" placeholder="Email"
                                                            id="email"
                                                            name="email"
                                                            type='email'
                                                            value={formUser.email || ''}
                                                            onChange={handleChangeEmail}
                                                            disabled={action === "Editar" ? true : false}
                                                            required
                                                            pattern=".*\S+.*" />
                                                        <div className="invalid-feedback" id="">
                                                            Campo requerido
                                                        </div>	
                                                    </div>
                                                    <div className="col-6">
                                                        <label className="form-label">Nombre de Usuario</label>
                                                        <input className="form-control" placeholder="Nombre"
                                                            id="username"
                                                            name="username"
                                                            value={formUser.username || ''}
                                                            onChange={handleChangeFormUser}
                                                            required                                                  
                                                            pattern=".*\S+.*"
                                                            disabled />
                                                        <div className="invalid-feedback" id="">
                                                            El nombre de usuario debe tener al menos 5 caracteres.
                                                        </div>	
                                                    </div>
                                                </div>
                                                
                                                <div className="mb-3 row" >
                                                    <div className='col-xl-6'>
                                                        <label className="form-label">Certificación</label>
                                                        <select className="form-select" name="certification" onChange={handleCertificationChange} value={selectedCertification} required disabled={action === "Editar" ? true : false}>
                                                            <option value="">Seleccione un opción</option>
                                                            <option value="2">EMeT</option>
                                                            <option value="1">EPSt</option>
                                                        </select>
                                                    </div>
                                                    <div className='col-xl-6'>
                                                        <label className="form-label">Tipo</label>
                                                        <select className="form-select" name="certification_type"  onChange={handleTypeChange} value={selectedType} required disabled={action === "Editar" ? true : false}>
                                                            <option value="">Seleccione un opción</option>
                                                            <option value="1">Certificación</option>
                                                            <option value="2">Diagnostico</option>
                                                            <option value="3">Prueba</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="mb-3 row" >
                                                    <div className='col-xl-6'>
                                                        <label className="form-label">Candidato</label>
                                                        <select className="form-select" name="candidate"  onChange={handleCandidateChange} value={selectedCandidate} required disabled={action === "Editar" ? true : false}>
                                                            <option value="">Seleccione un opción</option>
                                                            <option value="2">Adulto</option>
                                                            <option value="1">Young Learners</option>
                                                            <option value="3">Junior</option>
                                                        </select>
                                                    </div>
                                                    <div className='col-xl-6'>
                                                        <label className="form-label" htmlFor="timepicker3">Selecciona un rango de fechas</label>
                                                        <DateRangePicker onDateChange={handleDateChange} />
                                                    </div>
                                                </div>
                                                <div className="mb-3 row">
                                                    {
                                                        action === "Nuevo" ? (
                                                            <div className='col-xl-6'>
                                                                <SelectColleges setSharedCollege={setSharedCollege} />
                                                            </div>
                                                        ) : (
                                                            <div className="col-6">
                                                                <label className="form-label">Colegio</label>
                                                                <input className="form-control" placeholder="Colegio"
                                                                    id="college"
                                                                    name="college"
                                                                    type='text'
                                                                    value={formCollege.name || ''}
                                                                    disabled />
                                                            </div>
                                                        )
                                                    }
                                                    {
                                                        action === "Editar" && (
                                                            <div className='col-xl-6'>
                                                                <label className="form-label">Estatus</label>
                                                                <select className="form-select" name="status"  onChange={handleStatusChange} value={selectedStatus} >
                                                                    <option value="1">Activo</option>
                                                                    <option value="0">Inactivo</option>
                                                                </select>
                                                            </div>
                                                        )
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </Tab>
                            {action === "Nuevo" && (
                                <Tab eventKey="bulk" title="Carga masiva">
                                    <div className="mt-3">
                                        <form id="formBulkUpload" className="was-validated" >
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label className="form-label">Nombre remitente</label>
                                                    <input 
                                                        className="form-control" 
                                                        name="first_name" 
                                                        value={bulkForm.first_name} 
                                                        onChange={handleChangeBulk} 
                                                        required
                                                        pattern=".*\S+.*" />
                                                    <div className="invalid-feedback" id="">
                                                        Campo requerido
                                                    </div>	
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">Apellido remitente</label>
                                                    <input 
                                                        className="form-control" 
                                                        name="last_name" 
                                                        value={bulkForm.last_name} 
                                                        onChange={handleChangeBulk} 
                                                        required
                                                        pattern=".*\S+.*" />
                                                    <div className="invalid-feedback" id="">
                                                        Campo requerido
                                                    </div>	
                                                </div>
                                            </div>
                                            <div className="row mt-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">Correo receptor</label>
                                                    <input 
                                                        className="form-control" 
                                                        name="email" 
                                                        type="email" 
                                                        value={bulkForm.email} 
                                                        onChange={handleChangeBulk} 
                                                        required
                                                        pattern=".*\S+.*" />
                                                    <div className="invalid-feedback" id="">
                                                        Campo requerido
                                                    </div>	
                                                </div>
                                                <div className='col-xl-6'>
                                                    <SelectColleges setSharedCollege={setSharedCollege} />
                                                </div>
                                            </div>
                                            <div className="row mt-3" >
                                                <div className='col-xl-6'>
                                                    <label className="form-label">Certificación</label>
                                                    <select className="form-select" name="certification" onChange={handleCertificationBulkChange} value={selectedCertificationBulk} required >
                                                        <option value="">Seleccione un opción</option>
                                                        <option value="2">EMeT</option>
                                                        <option value="1">EPSt</option>
                                                    </select>
                                                </div>
                                                <div className='col-xl-6'>
                                                    <label className="form-label" htmlFor="timepicker3">Selecciona un rango de fechas</label>
                                                    <DateRangePicker onDateChange={handleDateBulkChange} />
                                                </div>
                                            </div>
                                            <div className="row mt-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">Archivo de candidatos (.xls o .xlsx)</label>
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        accept=".xls,.xlsx"
                                                        onChange={handleFileChange}
                                                        ref={fileInputRef}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </Tab>
                            )}
                        </Tabs>
                    </div>
                    <div className="modal-footer">
                    <button type="button" className="btn btn-outline-default" onClick={handleCloseModal}>Cerrar</button>
                        <button 
                            type="button" 
                            className="btn btn-outline-theme" 
                            onClick={handleSubmit} 
                            form={activeTab === 'individual' ? 'formCandidate' : 'formBulkUpload'}>
                            {action}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CandidateModal;